import { Form, Input, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm } from 'hooks';
import { AtsLabel, FormItem, ModalActions } from 'components';
import {
  DepartmentFragment,
  DepartmentsStatus,
  useUpdateDepartmentMutation,
} from 'api';
import {
  ErrorMessages,
  isDeactivatingDepartmentsWithRolesError,
  isDepartmentNameTakenError,
  showErrorNotification,
} from 'utils';

import './editDepartmentForm.less';

type PropsType = {
  editDepartmentInfo: DepartmentFragment;
  onComplete?: () => void;
  onCancel?: () => void;
};

const EditDepartmentForm = ({
  onComplete,
  onCancel,
  editDepartmentInfo,
}: PropsType) => {
  const { t } = useTranslation();
  const [form] = useForm();

  const [updateDepartment, { loading }] = useUpdateDepartmentMutation({
    onCompleted() {
      if (onComplete) {
        onComplete();
      }
    },
    onError({ graphQLErrors }) {
      graphQLErrors.forEach((error) => {
        if (isDepartmentNameTakenError(error)) {
          const name = form.getFieldValue('name');
          return form.setFields([
            {
              name: 'name',
              errors: [`${name} already exists. Please update the name.`],
            },
          ]);
        }

        if (isDeactivatingDepartmentsWithRolesError(error)) {
          return showErrorNotification({
            content:
              'There are active roles associated with this department. Please update the roles before deactivating this department. ',
          });
        }

        return showErrorNotification();
      });
    },
  });

  const handleCreateRole = ({
    name,
    isActive = true,
  }: {
    name: string;
    isActive?: boolean;
  }) =>
    updateDepartment({
      variables: {
        departmentId: editDepartmentInfo.id,
        input: {
          name: name.trim(),
          status: isActive
            ? DepartmentsStatus.Active
            : DepartmentsStatus.Closed,
        },
      },
    });

  return (
    <Form
      className="departments-form"
      form={form}
      onFinish={handleCreateRole}
      initialValues={{
        name: editDepartmentInfo?.name,
        isActive: editDepartmentInfo?.status === DepartmentsStatus.Active,
      }}
    >
      <div className="departments-form__status">
        <FormItem
          noStyle
          shouldUpdate={(prev, curr) => prev.isActive !== curr.isActive}
        >
          {({ getFieldValue }) => {
            const isActive = getFieldValue('isActive');

            return isActive ? 'Active' : 'Inactive';
          }}
        </FormItem>
        <FormItem
          name="isActive"
          valuePropName="checked"
          className="departments-form__status-switch"
        >
          <Switch defaultChecked />
        </FormItem>
      </div>

      <FormItem
        name="name"
        validateFirst
        rules={[
          {
            required: true,
            whitespace: true,
            message: ErrorMessages.EmptyDepartmentName,
          },
          {
            pattern: /^[a-z\d\s()\-_,.:;'/+*&]+$/i,
            message: ErrorMessages.InvalidDepartmentName,
          },
          {
            max: 120,
            message: ErrorMessages.InvalidDepartmentName,
          },
        ]}
      >
        <Input
          placeholder="Department name"
          suffix={
            editDepartmentInfo.ats ? (
              <AtsLabel
                title={t('Departments.atsLabelTollTip', {
                  ats: editDepartmentInfo?.ats,
                })}
              >
                {editDepartmentInfo?.ats}
              </AtsLabel>
            ) : null
          }
        />
      </FormItem>

      {/* Action buttons */}
      <FormItem noStyle shouldUpdate>
        {({ getFieldsError }) => (
          <ModalActions
            onCancel={onCancel}
            saveBtnText="Save Changes"
            saveBtnDisabled={
              !!getFieldsError().filter(({ errors }) => errors.length).length
            }
            saveBtnLoading={loading}
          />
        )}
      </FormItem>
    </Form>
  );
};

export default EditDepartmentForm;
